import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Beer, MapPin, Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { registerMember, playerGoogleLogin } from "@/lib/api";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1513807762437-8c8dee6b3776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxuZW9uJTIwcHViJTIwc2lnbnxlbnwwfHx8fDE3ODUzNjk1NjZ8MA&ixlib=rb-4.1.0&q=85";

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export default function Login() {
  const [handle, setHandle] = useState("");
  const [name, setName] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login } = useApp();
  const navigate = useNavigate();
  const processedHash = useRef(false);

  // Handle Google callback: /login#session_id=...
  useEffect(() => {
    const hash = window.location.hash || "";
    if (processedHash.current || !hash.includes("session_id=")) return;
    processedHash.current = true;
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    if (!sid) return;
    setGoogleLoading(true);
    playerGoogleLogin(sid)
      .then((res) => {
        if (res.banned) {
          toast.error("This account has been banned by an admin.");
          return;
        }
        login("@" + res.handle, res.name);
        toast.success(`Welcome, ${res.name}!`);
        navigate("/");
      })
      .catch(() => toast.error("Google sign-in failed. Please try again."))
      .finally(() => setGoogleLoading(false));
  }, [login, navigate]);

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async () => {
    const h = handle.trim().replace(/^@/, "");
    const n = name.trim();
    if (!h || !n) {
      toast.error("Grab a handle and a display name to enter.");
      return;
    }
    try {
      const res = await registerMember(h, n);
      if (res.banned) {
        toast.error("This handle has been banned by an admin.");
        return;
      }
    } catch (err) {
      console.warn("Member registration check failed, allowing entry:", err);
    }
    login("@" + h, n);
    toast.success(`Welcome to the pub, ${n}!`);
    navigate("/");
  };

  const playAsGuest = async () => {
    const suffix = Math.floor(1000 + Math.random() * 9000);
    const h = "guest" + suffix;
    try {
      await registerMember(h, "Guest " + suffix);
    } catch (err) {
      console.warn("Guest registration failed, continuing:", err);
    }
    login("@" + h, "Guest " + suffix);
    toast.success("Jumping in as a guest! 🍻");
    navigate("/");
  };

  return (
    <div className="max-w-md mx-auto min-h-screen relative flex flex-col justify-end">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
      <div className="absolute inset-0 bg-slate-950/85" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 p-8 pb-16"
      >
        <div className="flex items-center gap-2 mb-2">
          <Beer className="text-amber-400" size={34} strokeWidth={2.5} />
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-amber-400">Pub Quiz Nights</span>
        </div>
        <h1 className="font-display text-5xl font-black tracking-tight leading-none text-slate-50 mb-1">
          Appy<span className="text-amber-400">Our</span>
        </h1>
        <p className="text-slate-400 text-base mb-8">Find a quiz. Buzz in. Beat the room.</p>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2 block">Handle</label>
            <Input
              data-testid="login-handle-input"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="@quizhero"
              className="h-14 bg-slate-900 border-slate-700 text-lg rounded-2xl px-5 text-slate-50"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2 block">Display Name</label>
            <Input
              data-testid="login-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sam the Quizzer"
              onKeyDown={(e) => e.key === "Enter" && submit()}
              className="h-14 bg-slate-900 border-slate-700 text-lg rounded-2xl px-5 text-slate-50"
            />
          </div>
        </div>

        <motion.button
          data-testid="login-submit-btn"
          whileTap={{ scale: 0.96 }}
          animate={{ boxShadow: ["0 0 20px rgba(245,158,11,0.35)", "0 0 40px rgba(245,158,11,0.75)", "0 0 20px rgba(245,158,11,0.35)"] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          onClick={submit}
          className="mt-8 w-full h-[68px] bg-amber-400 text-slate-950 rounded-2xl font-display font-black text-2xl flex items-center justify-center gap-3 border-2 border-amber-300 no-tap-highlight"
        >
          <MapPin strokeWidth={3} size={26} />
          Find nearest pub quiz
        </motion.button>
        <p className="text-center text-slate-400 text-sm font-semibold mt-4">
          One tap. No password. No email. Just vibes.
        </p>

        <motion.button
          data-testid="login-guest-btn"
          whileTap={{ scale: 0.96 }}
          onClick={playAsGuest}
          className="mt-3 w-full h-12 bg-transparent text-slate-300 rounded-2xl font-bold text-base border border-slate-700 no-tap-highlight"
        >
          Play as Guest
        </motion.button>

        <div className="flex items-center gap-3 my-4">
          <div className="h-px flex-1 bg-slate-800" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-600">or</span>
          <div className="h-px flex-1 bg-slate-800" />
        </div>

        <motion.button
          data-testid="login-google-btn"
          whileTap={{ scale: 0.96 }}
          disabled={googleLoading}
          onClick={googleSignIn}
          className="w-full h-14 bg-white text-slate-900 rounded-2xl font-bold text-base flex items-center justify-center gap-3 disabled:opacity-70 no-tap-highlight"
        >
          {googleLoading ? <Loader2 className="animate-spin" /> : <GoogleIcon />}
          {googleLoading ? "Signing in…" : "Sign in / up with Google"}
        </motion.button>
      </motion.div>
    </div>
  );
}
